import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, Eyebrow } from "../components/Section";

const BOARDROOM_IMG =
  "https://images.pexels.com/photos/28506813/pexels-photo-28506813.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About"
        num="01"
        title={<>About <span className="italic text-[var(--bjc-gold)]">Bharat Jeswani Consulting</span>.</>}
        intro="A specialist global advisory firm focused on AML, financial crime risk, and regulatory defence — built for institutions, boards, and practitioners who require clarity over comfort."
      />

      {/* Overview */}
      <section className="bjc-section border-b border-[var(--bjc-border)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Eyebrow num="02">Overview</Eyebrow>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-8">
            <p className="text-xl md:text-2xl font-serif-display text-[var(--bjc-text)] leading-snug">
              The firm works with institutions to strengthen control frameworks, independently
              assess compliance effectiveness, and respond to complex regulatory and financial
              crime challenges.
            </p>
            <p className="text-base text-[var(--bjc-text-muted)] leading-relaxed">
              We do not generalise. Our remit begins where AML and financial crime risk are
              already serious — and end where the institution can defend its position with
              evidence, governance, and controls that have been tested in practice.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="bjc-section border-b border-[var(--bjc-border)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Eyebrow num="03">Philosophy</Eyebrow>
            <h2 className="mt-6 font-serif-display text-3xl md:text-5xl leading-[1.04] text-[var(--bjc-text)]">
              Compliance must be effective, not merely <em className="text-[var(--bjc-gold)] not-italic">documented</em>.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <ul className="space-y-5">
              {[
                ["Control Effectiveness", "Whether a program meaningfully detects, deters, and disrupts financial crime — not whether it is on paper."],
                ["Governance Clarity", "Roles, escalations, and decisions that withstand scrutiny from boards, auditors, and regulators."],
                ["Regulatory Defensibility", "Documentation, evidence, and rationale that hold up when tested in inspection or investigation."],
                ["Practical Implementation", "Recommendations that are operable inside the institution — not theoretical or out of context."],
              ].map(([t, b], i) => (
                <li key={t} data-testid={`philosophy-${i}`} className="flex items-start gap-6 border-t border-[var(--bjc-border)] pt-5">
                  <span className="bjc-numeral pt-1">0{i + 1}</span>
                  <div>
                    <h3 className="font-serif-display text-xl md:text-2xl text-[var(--bjc-text)]">{t}</h3>
                    <p className="mt-2 text-sm md:text-base text-[var(--bjc-text-muted)] leading-relaxed">{b}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="bjc-section border-b border-[var(--bjc-border)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <Eyebrow num="04">Founder</Eyebrow>
            <div className="mt-10 inline-flex items-center gap-4 p-4 border border-[var(--bjc-border)] bg-[var(--bjc-surface)]">
              <img
                src="/assets/founder.jpg"
                alt="Bharat Jeswani"
                data-testid="about-founder-photo"
                className="w-20 h-20 rounded-full object-cover border border-[var(--bjc-gold)]/40 grayscale hover:grayscale-0 transition-all duration-700"
              />
              <div className="pr-2">
                <div className="font-serif-display text-2xl text-[var(--bjc-text)] leading-tight">
                  Bharat Jeswani
                </div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--bjc-text-muted)] mt-2">
                  Chartered Accountant
                </div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--bjc-gold)] mt-1">
                  Author · The Frauditor
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <h2 className="font-serif-display text-3xl md:text-5xl lg:text-6xl leading-[1.04] text-[var(--bjc-text)]">
              A practitioner-led <span className="italic text-[var(--bjc-gold)]">practice</span>.
            </h2>
            <div className="mt-10 space-y-5 text-base md:text-lg text-[var(--bjc-text-muted)] leading-relaxed">
              <p>
                <span className="text-[var(--bjc-text)]">Bharat Jeswani</span> is a Chartered Accountant
                and financial crime specialist with deep expertise in forensic accounting, AML, and
                regulatory advisory.
              </p>
              <p>
                He has advised institutions across sectors on fraud investigations, AML compliance,
                and financial crime risk, and is a faculty member for forensic accounting programs.
              </p>
              <p>
                He is also the author of <em className="not-italic text-[var(--bjc-text)]">The Frauditor</em>,
                a widely recognised publication in the field.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Global Perspective */}
      <section className="bjc-section relative overflow-hidden border-b border-[var(--bjc-border)]">
        <div className="absolute inset-0 -z-10 opacity-15">
          <img src={BOARDROOM_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[var(--bjc-bg)]/80" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Eyebrow num="05">Global Perspective</Eyebrow>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-6">
            <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.04] text-[var(--bjc-text)]">
              India · Middle East · Asia-Pacific.
            </h2>
            <p className="text-lg text-[var(--bjc-text-muted)] leading-relaxed">
              BJC operates with a global outlook, supporting clients across India, the Middle East,
              and Asia-Pacific, with a focus on cross-border financial crime risks and evolving
              regulatory expectations.
            </p>
            <div className="mt-8">
              <Link to="/contact" data-testid="about-cta" className="bjc-link">
                Begin an Engagement <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
